"use client";

import { useEffect, useMemo, useRef } from "react";
import { useMidiInputs, useStudio } from "@/contexts/studio-context";
import {
  HandsOnGate,
  type HandsOnEvent,
} from "@/lib/learning-journey/hands-on-gate";
import type { PianoEvent } from "@/lib/piano/piano";

/**
 * The two things a player can answer with, without taking their hands off the
 * keys: **the pedal**, and **playing**.
 *
 * Every screen of the parcours ends on the same question — do it again, or
 * move on — and until now both answers were a click. On a piano that means
 * lifting a hand, finding a pointer, coming back, and re-placing eight
 * fingers, four times per run. The gesture is not a shortcut for the buttons;
 * it is what the buttons were standing in for.
 *
 * ## The rule, and it is one sentence
 *
 * **The pedal always goes forward. Playing relaunches.**
 *
 * Forward is whatever the screen offers next — continue, start the pass, next
 * phrase — and it is never destructive: no screen maps the pedal onto going
 * back, redoing from scratch, or leaving. A foot cannot aim, so it is only
 * ever given the act the player would have taken anyway.
 *
 * Playing is only listened to where playing means something: the cards that
 * offer another pass, and the screen waiting for one. On the reading screens
 * — S0, phrase validated, the harmonic intro — the player is exploring the
 * keyboard, and hearing a chord must not walk the journey forward.
 *
 * ## Why the pedal, and not a key
 *
 * CC64 has been arriving at `Piano.sustainOn()` since the MIDI input was
 * written (`lib/piano/input/midi-shared.ts`), and nothing but the audio
 * controller ever listened. At the moment these screens are up the transport
 * is stopped: the pedal has no musical sense left to lose, and it is the one
 * control on the instrument the hands never have to reach for.
 *
 * Designating a key instead — the lowest A, the highest C — would have cost a
 * range the player does not always own (61-key boards), a note the phrase
 * might legitimately use, and an explanation.
 */

/**
 * Nothing counts for this long after a screen arms.
 *
 * A run ends **on** a press, and a pass in time typically ends with the pedal
 * still going down under the last chord. Without the guard, the card that
 * just appeared would read the very gesture that produced it as an answer to
 * itself and skip a step on a single note.
 */
const ARM_MS = 450;

export type HandsOnControlsArgs = {
  /** Off while a pass is running: the pedal is the player's again. */
  readonly enabled: boolean;
  /** What the pedal does here. Always forward, never destructive. */
  readonly onAdvance?: (() => void) | null;
  /** What playing does here, or `null` when playing must mean nothing. */
  readonly onPlay?: (() => void) | null;
  /**
   * The notes that have to be **held together** for `onPlay` to fire, or
   * `null` for any single note.
   *
   * A card that just ended a pass takes anything: the hands are already on
   * the phrase and whatever they strike next is a restart. A screen still
   * *waiting* for its first pass asks for the opening group, because there
   * the player has a reason to try a key before committing — the panel is
   * showing them the keyboard to read.
   */
  readonly playMidis?: readonly number[] | null;
};

export function useHandsOnControls({
  enabled,
  onAdvance = null,
  onPlay = null,
  playMidis = null,
}: HandsOnControlsArgs): void {
  const { studio } = useStudio();

  const callbacks = useRef({ onAdvance, onPlay });

  useEffect(() => {
    callbacks.current = { onAdvance, onPlay };
  }, [onAdvance, onPlay]);

  const wanted = useMemo(
    () => (playMidis === null ? null : [...playMidis].sort((a, b) => a - b)),
    [playMidis],
  );
  const wantedKey = wanted === null ? "" : wanted.join(",");

  // A boolean and not the callback itself: re-arming on every new closure
  // would restart the guard window, and a parent that re-renders every 100 ms
  // would keep the controls permanently deaf.
  const listensToPlay = onPlay !== null;

  useEffect(() => {
    if (!enabled || studio === null) {
      return;
    }

    const piano = studio.getPiano();
    const gate = new HandsOnGate({
      armedAt: Date.now(),
      armMs: ARM_MS,
      // Seeded from the instrument, so a pedal already down when the screen
      // appeared has to come back up before it can say anything.
      pedalDown: piano.isSustainActive(),
      held: piano.getPressedMidis().keys(),
      playMidis: wantedKey === "" ? null : wantedKey.split(",").map(Number),
      listensToPlay,
    });

    return piano.on((event) => {
      const gesture = toGesture(event);

      if (gesture === null) {
        return;
      }

      const action = gate.read(gesture, Date.now());

      if (action === "advance") {
        callbacks.current.onAdvance?.();

        return;
      }

      if (action === "play") {
        callbacks.current.onPlay?.();
      }
    });
  }, [enabled, studio, wantedKey, listensToPlay]);
}

/**
 * The piano's bus, reduced to the four gestures the gate knows.
 *
 * Only the player's own keys. The application sounds notes on the same piano
 * — the audition, the hand it accompanies with — and they are not answers.
 */
function toGesture(event: PianoEvent): HandsOnEvent | null {
  if (event.type === "sustain_on") {
    return { kind: "pedalDown" };
  }

  if (event.type === "sustain_off") {
    return { kind: "pedalUp" };
  }

  if (event.type !== "press" && event.type !== "release") {
    return null;
  }

  if (event.origin !== "player") {
    return null;
  }

  return { kind: event.type, midi: event.note.toMidi() };
}

/**
 * Whether there is an instrument on the other end.
 *
 * Gates what is *said* and what happens *on its own* — never the gestures
 * themselves, which cost nothing to leave listening. Someone on the computer
 * keyboard has `Entrée` under a finger and needs neither the reminder nor a
 * screen that advances by itself.
 */
export function useHandsOnPiano(): boolean {
  const { devices } = useMidiInputs();

  return devices.some((device) => device.connected && device.enabled);
}
